/**
 * Intent-Routing: einfache Heuristik ohne LLM.
 * WICHTIG: Nur aufrufen wenn chatFlowState.active === false.
 * Bei aktivem Flow keine Intent-Erkennung – jede Aktion geht an den Wizard.
 *
 * "plane" + turnier/schleifchen/... => plan_tournament;
 * area-basiert => Flow starten oder Modul-Auswahl (aus flow.areas).
 */

import {
  detectTrainerListQuery,
  detectWeekSessionsQuery,
  extractTrainingSchedulePlayerNameFromText,
  shouldBlockTrainingFollowUpForInvoiceContext,
  detectInvoiceQuery,
  detectInvoiceListOpenReply,
  detectCoachDeskPersonNameReply,
  detectCoachDeskDisambiguationReply,
} from "@tennis-matches/services/ai-chat";
import { sanitizePlayerNameQuery } from "@tennis-matches/services/trainerstunden/peopleNameSearch";

export {
  detectTrainerListQuery,
  detectWeekSessionsQuery,
  detectInvoiceQuery,
  detectInvoiceListOpenReply,
  detectCoachDeskPersonNameReply,
  detectCoachDeskDisambiguationReply,
};
import { getFlowIdForArea, TOURNAMENT_AREAS } from "./flow.areas.js";
import { PLAN, TOURNAMENT, TRAINING } from "../intentKeywords.js";

const OPEN_VERBS = [
  "öffnen",
  "open",
  "zeig",
  "gehe zu",
  "geh zu",
  "starte",
  "wechsle",
  "navigier",
];

/** @type {Array<{ keys: string[]; route: string }>} */
const OPEN_APP_ROUTES = [
  {
    keys: ["trainingsdesk", "trainerstunden", "coachdesk"],
    route: "trainerstunden",
  },
  { keys: ["schleifchen", "matchmixer"], route: "schleifchen-setup" },
  {
    keys: ["clubmeisterschaft", "clubmeisterschaf", "courtleague", "clubchamp"],
    route: "clubmeisterschaft-setup",
  },
  { keys: ["tennis live", "ltr"], route: "ltr" },
  { keys: ["hub", "startseite"], route: "hub" },
  { keys: ["medenspiel", "teamdesk"], route: "medenspiele" },
];

/**
 * @param {string} text
 * @returns {string | null} route key for navigateTo
 */
export function detectOpenAppRoute(text) {
  if (!text || typeof text !== "string") return null;
  const t = text.trim().toLowerCase();
  const wantsOpen = OPEN_VERBS.some((v) => t.includes(v));
  if (!wantsOpen) return null;
  for (const entry of OPEN_APP_ROUTES) {
    if (entry.keys.some((k) => t.includes(k))) return entry.route;
  }
  return null;
}

function cleanExtractedName(rawName) {
  const name = sanitizePlayerNameQuery(
    String(rawName ?? "")
      .trim()
      .replace(/\s+training$/i, "")
      .replace(/[?.!]+$/, "")
      .trim(),
  );
  return name.length >= 2 ? name : null;
}

/**
 * @param {string} text
 */
function isLikelyPersonFullName(text) {
  const parts = String(text ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length < 2 || parts.length > 4) return false;
  return parts.every((p) => /^[\p{L}][\p{L}\-'.]*$/u.test(p));
}

/**
 * @param {string} text
 */
function isLikelyPersonFirstName(text) {
  const part = sanitizePlayerNameQuery(String(text ?? "").trim());
  return part.length >= 2 && /^[\p{L}][\p{L}\-'.]*$/u.test(part);
}

/**
 * @param {string} text
 * @param {{ priorMessages?: Array<{ role?: string, content?: string }> }} [context]
 * @returns {string | null}
 */
export function extractTrainingFollowUpName(text, context = {}) {
  const raw = String(text ?? "").trim();
  if (!raw) return null;

  const undMatch = raw.match(
    /^(?:und|auch)\s+(?:wann\s+trainiert\s+(?:nun\s+|denn\s+|noch\s+|mal\s+)?)?(.+?)(?:\?|$)/i,
  );
  if (undMatch?.[1]) {
    const name = cleanExtractedName(undMatch[1]);
    if (name && (isLikelyPersonFullName(name) || isLikelyPersonFirstName(name)))
      return name;
  }

  const prior = Array.isArray(context.priorMessages)
    ? context.priorMessages
    : [];
  if (shouldBlockTrainingFollowUpForInvoiceContext(raw, prior)) return null;

  const hadTrainingContext = prior.some((m) => {
    if (m?.kind === "training") return true;
    if (Array.isArray(m?.ambiguousPeople) && m.ambiguousPeople.length)
      return true;
    const c = String(m?.content ?? "").toLowerCase();
    return /(wann|training|trainiert|termine|trainings|mehrere passende)/.test(
      c,
    );
  });
  if (!hadTrainingContext) return null;

  const trainingInline = raw.match(
    /wann\s+trainiert\s+(?:nun\s+|denn\s+|noch\s+|mal\s+)?(.+?)(?:\?|$)/i,
  );
  if (trainingInline?.[1]) {
    const name = cleanExtractedName(trainingInline[1]);
    if (name) return name;
  }

  const bare = cleanExtractedName(raw.replace(/[?.!]+$/, "").trim());
  if (bare && (isLikelyPersonFullName(bare) || isLikelyPersonFirstName(bare)))
    return bare;
  return null;
}

/**
 * @param {string} text
 * @param {{ priorMessages?: Array<{ role?: string, content?: string, pendingTrainingConfirm?: { personId: string, playerName: string } }> }} [context]
 * @returns {string | null}
 */
export function extractTrainingSchedulePlayerName(text, context = {}) {
  if (!text || typeof text !== "string") return null;
  const raw = text.trim();
  if (!raw) return null;
  const t = raw.toLowerCase();

  if (!/(wann|training|trainiert|termine|trainings)/.test(t)) {
    return extractTrainingFollowUpName(raw, context);
  }

  return extractTrainingSchedulePlayerNameFromText(raw);
}

/**
 * @param {string} text
 * @param {Array<{ role?: string, content?: string, pendingTrainingConfirm?: { personId: string, playerName: string } }>} priorMessages
 * @returns {{ personId: string, playerName: string } | null}
 */
export function findPendingTrainingConfirm(priorMessages) {
  const list = Array.isArray(priorMessages) ? priorMessages : [];
  if (shouldBlockTrainingFollowUpForInvoiceContext("", list)) return null;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const m = list[i];
    if (m?.role !== "assistant") continue;
    if (m.pendingTrainingConfirm?.personId) return m.pendingTrainingConfirm;
    const content = String(m.content ?? "");
    const match = /^Meinst du (.+)\?$/.exec(content.trim());
    if (match?.[1]) {
      return { personId: "", playerName: match[1].trim() };
    }
  }
  return null;
}

/**
 * @param {string} text
 * @param {Array<{ role?: string, content?: string, pendingTrainingConfirm?: object }>} priorMessages
 * @returns {{ personId: string, playerName: string } | null}
 */
export function detectTrainingConfirmReply(text, priorMessages) {
  const raw = String(text ?? "")
    .trim()
    .toLowerCase();
  if (!/^(ja|genau|richtig|jep|yes|ok|okay|stimmt|korrekt)\.?$/.test(raw))
    return null;
  return findPendingTrainingConfirm(priorMessages);
}

/**
 * @param {string} text
 * @returns {{ type: "ask-name" } | { type: "lookup", query: string } | null}
 */
export function detectCoachDeskStatusQuery(text) {
  if (!text || typeof text !== "string") return null;
  const raw = text.trim();
  const t = raw.toLowerCase();

  if (
    /status\s+zu\s+(?:einem\s+)?spieler/i.test(t) &&
    !/(?:für|von)\s+\S{2,}/i.test(raw)
  ) {
    return { type: "ask-name" };
  }
  if (/(?:rechnungs)?entwürfe.*(?:für|von)\s+einem\s+spieler/i.test(t)) {
    return { type: "ask-name" };
  }

  const patterns = [
    /status\s+(?:zu|für|von)\s+(.+?)(?:\?|$)/i,
    /(?:rechnungs)?entwürfe\s+(?:für|von)\s+(.+?)(?:\?|$)/i,
    /(?:termine|rechnungen|versand)\s+(?:für|von)\s+(.+?)(?:\?|$)/i,
  ];

  for (const re of patterns) {
    const m = raw.match(re);
    if (m?.[1]) {
      const name = sanitizePlayerNameQuery(
        String(m[1])
          .replace(/[?.!]+$/, "")
          .trim(),
      );
      if (/^einem?\s+spieler$/i.test(name)) return { type: "ask-name" };
      if (name.length >= 2) return { type: "lookup", query: name };
    }
  }
  return null;
}

/**
 * @param {string} text
 * @returns {"plan_tournament" | "plan_training" | null}
 */
export function detectIntent(text) {
  if (!text || typeof text !== "string") return null;
  const t = text.trim().toLowerCase();
  const hasPlan = PLAN.some((kw) => t.includes(kw));
  const hasTournament = TOURNAMENT.some((kw) => t.includes(kw));
  const hasTraining = TRAINING.some((kw) => t.includes(kw));
  if (hasPlan && hasTournament) return "plan_tournament";
  if (hasTraining || (hasPlan && t.includes("train"))) return "plan_training";
  return null;
}

/**
 * @param {"plan_tournament" | "plan_training"} intent
 * @param {{ area?: string }} pageContext
 * @returns {{ startFlowId?: string; assistantTextKey?: string; quickReplies?: Array<{ id: string; labelKey: string; value: string }> }}
 */
const CHOOSE_MODULE_LABEL_KEYS = {
  schleifchen: "ai.flow.common.chooseModule.schleifchen",
  clubmeisterschaft: "ai.flow.common.chooseModule.clubmeisterschaft",
};

export function routeIntent(intent, pageContext) {
  if (intent === "plan_tournament") {
    const area = String(pageContext?.area ?? "").toLowerCase();
    const normalizedArea = area === "club" ? "clubmeisterschaft" : area;
    const flowId = getFlowIdForArea(normalizedArea);
    if (flowId) {
      return { startFlowId: flowId };
    }
    return {
      assistantTextKey: "ai.flow.common.chooseModule.title",
      quickReplies: TOURNAMENT_AREAS.map((a) => ({
        id: `choose-${a}`,
        labelKey:
          CHOOSE_MODULE_LABEL_KEYS[a] || `ai.flow.common.chooseModule.${a}`,
        value: a,
      })),
    };
  }
  if (intent === "plan_training") {
    return {};
  }
  return {};
}
